import { useAdminInsurerType, useDownloadInsurersCsv } from '@/hooks/admin';
import { usePrefectures } from '@/hooks/useAddressSearch';
import { InsurerFilterParams } from '@/types/admin-insurer';
import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { AppAdminFormLabel, AppButton, AppSelect, SearchTextField } from '../atoms';
import { Download, Search } from '../atoms/AppIcon';
import { RA2NDatePicker } from './RA2NDatePicker';
import { useTheme } from '@/shared/providers/theme';

interface AdminInsurerFilterProps {
  onSearch?: (filters: InsurerFilterParams) => void;
}

const AdminInsurerFilter = ({ onSearch }: AdminInsurerFilterProps) => {
  const { site } = useParams<{ site: string }>();
  const currentSite = site ?? 'medix';
  const [searchParams] = useSearchParams();
  const { colors } = useTheme();

  const { data: prefectures } = usePrefectures({ siteType: currentSite, isAdmin: true });
  const { data: insurerTypes } = useAdminInsurerType(currentSite);
  const { mutate: downloadCsv, isPending: isDownloading } = useDownloadInsurersCsv();

  // Local state for filter fields
  const [filters, setFilters] = useState<InsurerFilterParams>({
    search_insurer_number: searchParams.get('search_insurer_number') || '',
    search_insurer_name: searchParams.get('search_insurer_name') || '',
    search_insurer_name_kana: searchParams.get('search_insurer_name_kana') || '',
    available_date: searchParams.get('available_date') || '',
    search_insurer_type_id: searchParams.get('search_insurer_type_id') || '',
    prefecture_id: searchParams.get('prefecture_id') || '',
  });

  // Sync local state if searchParams change externally (e.g. browser back button)
  useEffect(() => {
    setFilters({
      search_insurer_number: searchParams.get('search_insurer_number') || '',
      search_insurer_name: searchParams.get('search_insurer_name') || '',
      search_insurer_name_kana: searchParams.get('search_insurer_name_kana') || '',
      available_date: searchParams.get('available_date') || '',
      search_insurer_type_id: searchParams.get('search_insurer_type_id') || '',
      prefecture_id: searchParams.get('prefecture_id') || '',
    });
  }, [searchParams]);

  const prefectureOptions = prefectures?.map(prefecture => ({
    value: prefecture.id,
    label: prefecture.name,
  }));

  const insurerTypeOptions = insurerTypes?.map(insurerType => ({
    value: insurerType.id,
    label: insurerType.name,
  }));

  const handleSearchClick = () => {
    const trimmedFilters = {
      ...filters,
      search_insurer_number: filters.search_insurer_number?.trim() || '',
      search_insurer_name: filters.search_insurer_name?.trim() || '',
      search_insurer_name_kana: filters.search_insurer_name_kana?.trim() || '',
    };
    setFilters(trimmedFilters);
    onSearch?.(trimmedFilters);
  };

  const handleCsvClick = () => {
    downloadCsv(filters);
  };

  const handleFilterChange = (key: keyof InsurerFilterParams, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const target = e.currentTarget;
      handleSearchClick();
      requestAnimationFrame(() => target.focus());
    }
  };

  return (
    <div className="p-4 mb-4 bg-white border rounded-md">
      <div className="grid items-end grid-cols-4 gap-4">
        <div>
          <AppAdminFormLabel label="保険者番号" />
          <SearchTextField
            placeholder="保険者番号"
            value={filters.search_insurer_number}
            onChange={e => handleFilterChange('search_insurer_number', e.target.value)}
            onKeyDown={handleKeyDown}
          />
        </div>
        <div>
          <AppAdminFormLabel label="保険者名称" />
          <SearchTextField
            placeholder="保険者名称"
            value={filters.search_insurer_name}
            onChange={e => handleFilterChange('search_insurer_name', e.target.value)}
            onKeyDown={handleKeyDown}
          />
        </div>
        <div>
          <AppAdminFormLabel label="保険者名カナ" />
          <SearchTextField
            placeholder="保険者名カナ"
            value={filters.search_insurer_name_kana}
            onChange={e => handleFilterChange('search_insurer_name_kana', e.target.value)}
            onKeyDown={handleKeyDown}
          />
        </div>
        <div className="col-span-1" />
        <div>
          <AppAdminFormLabel label="基準日" />
          <RA2NDatePicker
            placeholder="基準日"
            value={filters.available_date}
            onChange={val => handleFilterChange('available_date', val)}
          />
        </div>
        <div>
          <AppAdminFormLabel label="保険種別" />
          <AppSelect
            placeholder="保険種別"
            options={insurerTypeOptions}
            style={{ width: '100%' }}
            value={
              filters.search_insurer_type_id ? Number(filters.search_insurer_type_id) : undefined
            }
            onChange={val => handleFilterChange('search_insurer_type_id', (val as string) || '')}
            allowClear
          />
        </div>
        <div>
          <AppAdminFormLabel label="都道府県" />
          <AppSelect
            placeholder="都道府県"
            style={{ width: '100%' }}
            options={prefectureOptions}
            value={filters.prefecture_id ? Number(filters.prefecture_id) : undefined}
            onChange={val => handleFilterChange('prefecture_id', val ? val.toString() : '')}
            allowClear
          />
        </div>
        <div className="flex gap-2">
          <AppButton onClick={handleSearchClick} icon={<Search />} type="primary">
            検索
          </AppButton>
          <AppButton
            onClick={handleCsvClick}
            icon={<Download />}
            style={
              {
                backgroundColor: colors.secondary,
                ['--action-bg-hover' as string]: colors.secondaryHover,
              } as React.CSSProperties
            }
            className="hover:!bg-[var(--action-bg-hover)]"
            type="primary"
            disabled={isDownloading}
            loading={isDownloading}
          >
            CSV
          </AppButton>
        </div>
      </div>
    </div>
  );
};

export default AdminInsurerFilter;
